import { useEffect, useState } from "react";
import { ReceiptText, Plus, Loader2, Search, Trash2, Download, X } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, downloadPdf } from "@/lib/api";
import { INSURERS } from "@/pages/Patients";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_STYLES = {
    draft: "bg-slate-100 text-slate-600 border-slate-200",
    submitted: "bg-blue-50 text-blue-700 border-blue-200",
    preauth_pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    denied: "bg-red-50 text-red-700 border-red-200",
    paid: "bg-emerald-100 text-emerald-800 border-emerald-300",
};
const STATUSES = Object.keys(STATUS_STYLES);
const EMPTY_ITEM = { description: "", code: "", qty: 1, rate: "" };

export default function Superbills() {
    const today = new Date().toISOString().slice(0, 10);
    const [bills, setBills] = useState(null);
    const [statusFilter, setStatusFilter] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [saving, setSaving] = useState(false);
    const [patientQuery, setPatientQuery] = useState("");
    const [patientResults, setPatientResults] = useState([]);
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState({ date: today, insurer: "", claim_no: "", preauth_no: "", notes: "" });
    const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
    const [icdQ, setIcdQ] = useState("");
    const [icdResults, setIcdResults] = useState([]);
    const [icdCodes, setIcdCodes] = useState([]);

    const load = async () => {
        try {
            const { data } = await api.get("/superbills", { params: { status: statusFilter } });
            setBills(data);
        } catch (e) { toast.error(fmtErr(e)); }
    };
    useEffect(() => { load(); }, [statusFilter]);

    useEffect(() => {
        if (!patientQuery.trim() || selected) { setPatientResults([]); return; }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/patients", { params: { search: patientQuery } });
                setPatientResults(data);
            } catch { setPatientResults([]); }
        }, 250);
        return () => clearTimeout(t);
    }, [patientQuery, selected]);

    useEffect(() => {
        if (!icdQ.trim()) { setIcdResults([]); return; }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/icd", { params: { q: icdQ } });
                setIcdResults(data);
            } catch { setIcdResults([]); }
        }, 250);
        return () => clearTimeout(t);
    }, [icdQ]);

    const total = items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.rate) || 0), 0);

    const setItem = (i, k, v) => setItems(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));

    const reset = () => {
        setSelected(null); setPatientQuery(""); setForm({ date: today, insurer: "", claim_no: "", preauth_no: "", notes: "" });
        setItems([{ ...EMPTY_ITEM }]); setIcdCodes([]); setIcdQ("");
    };

    const create = async (e) => {
        e.preventDefault();
        if (!selected) { toast.error("Select a patient"); return; }
        if (!items.some((i) => i.description.trim())) { toast.error("Add at least one line item"); return; }
        setSaving(true);
        try {
            const { data } = await api.post("/superbills", {
                patient_id: selected.id, date: form.date,
                insurer: form.insurer || selected.insurance_company || "",
                claim_no: form.claim_no, preauth_no: form.preauth_no, notes: form.notes,
                items: items.filter((i) => i.description.trim()), icd_codes: icdCodes,
            });
            toast.success(`Superbill ${data.bill_no} created — ₹${data.total.toLocaleString("en-IN")}`);
            setShowNew(false);
            reset();
            load();
        } catch (err) { toast.error(fmtErr(err)); } finally { setSaving(false); }
    };

    const setStatus = async (b, status) => {
        try {
            await api.put(`/superbills/${b.id}`, { status });
            toast.success(`${b.bill_no} → ${status}`);
            load();
        } catch (e) { toast.error(fmtErr(e)); }
    };

    const remove = async (b) => {
        if (!window.confirm(`Delete ${b.bill_no} (${b.patient_name})?`)) return;
        try {
            await api.delete(`/superbills/${b.id}`);
            load();
        } catch (e) { toast.error(fmtErr(e)); }
    };

    return (
        <div className="space-y-5 animate-fade-up" data-testid="superbills-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Superbills & Claims</h1>
                    <p className="mt-1 text-base text-slate-500">Itemized insurance superbills with ICD-10 linking and claim tracking</p>
                </div>
                <Button data-testid="new-superbill-btn" onClick={() => setShowNew(true)} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-2"><Plus className="h-4 w-4" /> New Superbill</Button>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="superbills-list-card">
                <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
                    <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><ReceiptText className="h-4 w-4 text-[#E06D1F]" /> Claims</h3>
                    <select data-testid="superbill-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-lg border border-slate-300 bg-white py-2 px-3 text-sm shadow-sm focus:border-orange-500 outline-none">
                        <option value="">All statuses</option>
                        {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                </div>
                {!bills ? (
                    <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#E06D1F]" /></div>
                ) : bills.length === 0 ? (
                    <p className="px-5 py-10 text-center text-sm text-slate-500" data-testid="superbills-empty">No superbills yet. Create one to start claim tracking.</p>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {bills.map((b) => (
                            <div key={b.id} data-testid={`superbill-row-${b.bill_no}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
                                <div>
                                    <p className="text-sm font-medium text-slate-900">
                                        <span className="font-mono2 text-[#C2410C]">{b.bill_no}</span> — {b.patient_name} <span className="font-mono2 text-xs text-slate-400">{b.patient_uid}</span>
                                        <span className={`ml-2 rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${STATUS_STYLES[b.status]}`} data-testid={`superbill-status-${b.bill_no}`}>{b.status.replace("_", " ")}</span>
                                    </p>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        {b.date} · ₹{Number(b.total).toLocaleString("en-IN")} · {b.items.length} item{b.items.length === 1 ? "" : "s"}
                                        {b.insurer ? ` · ${b.insurer}` : ""}{b.icd_codes?.length ? ` · ICD: ${b.icd_codes.join(", ")}` : ""}{b.claim_no ? ` · Claim ${b.claim_no}` : ""}
                                    </p>
                                </div>
                                <div className="flex gap-1.5 items-center">
                                    <select data-testid={`superbill-status-select-${b.bill_no}`} value={b.status} onChange={(e) => setStatus(b, e.target.value)} className="rounded-lg border border-slate-300 bg-white py-1.5 px-2 text-xs shadow-sm focus:border-orange-500 outline-none capitalize">
                                        {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                                    </select>
                                    <Button size="sm" variant="outline" data-testid={`superbill-pdf-${b.bill_no}`} onClick={() => downloadPdf(`/superbills/${b.id}/pdf`, `${b.bill_no}.pdf`).catch((e) => toast.error(fmtErr(e)))} className="gap-1 text-xs"><Download className="h-3.5 w-3.5" /> PDF</Button>
                                    <Button size="sm" variant="ghost" data-testid={`superbill-delete-${b.bill_no}`} onClick={() => remove(b)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Dialog open={showNew} onOpenChange={(o) => { setShowNew(o); if (!o) reset(); }}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="superbill-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">New Superbill</DialogTitle>
                        <DialogDescription>Itemized statement for insurance reimbursement. Amounts compute automatically.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={create} className="space-y-4">
                        <div className="space-y-1.5 relative">
                            <Label>Patient *</Label>
                            {selected ? (
                                <div className="flex items-center justify-between rounded-lg border border-orange-200 bg-[#FFF4EC] px-3 py-2.5" data-testid="sb-selected-patient">
                                    <span className="text-sm font-medium text-slate-900">{selected.name} <span className="font-mono2 text-xs text-[#C2410C]">{selected.patient_uid}</span></span>
                                    <button type="button" data-testid="sb-clear-patient" onClick={() => { setSelected(null); setPatientQuery(""); }} className="text-xs text-slate-500 hover:text-red-600">Change</button>
                                </div>
                            ) : (
                                <>
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                        <input data-testid="sb-patient-search" value={patientQuery} onChange={(e) => setPatientQuery(e.target.value)} placeholder="Search patient..." className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-9 pr-3.5 text-sm shadow-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none placeholder:text-slate-400" />
                                    </div>
                                    {patientResults.length > 0 && (
                                        <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-52 overflow-y-auto" data-testid="sb-patient-results">
                                            {patientResults.map((p) => (
                                                <button key={p.id} type="button" data-testid={`sb-patient-option-${p.patient_uid}`} onClick={() => { setSelected(p); setPatientResults([]); if (p.insurance_company) setForm((f) => ({ ...f, insurer: p.insurance_company })); }} className="w-full text-left px-4 py-2.5 hover:bg-[#FFF4EC] border-b border-slate-100 last:border-0">
                                                    <span className="text-sm font-medium text-slate-900">{p.name}</span>
                                                    <span className="ml-2 text-xs text-slate-500 font-mono2">{p.patient_uid}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div className="space-y-1.5"><Label>Date *</Label><Input data-testid="sb-date-input" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                            <div className="space-y-1.5">
                                <Label>Insurer</Label>
                                <select data-testid="sb-insurer-select" value={form.insurer} onChange={(e) => setForm({ ...form, insurer: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3 text-sm shadow-sm focus:border-orange-500 outline-none">
                                    <option value="">Self / None</option>
                                    {INSURERS.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div className="space-y-1.5"><Label>Claim No</Label><Input data-testid="sb-claim-input" value={form.claim_no} onChange={(e) => setForm({ ...form, claim_no: e.target.value })} /></div>
                            <div className="space-y-1.5"><Label>Pre-Auth No</Label><Input data-testid="sb-preauth-input" value={form.preauth_no} onChange={(e) => setForm({ ...form, preauth_no: e.target.value })} /></div>
                        </div>

                        <div className="space-y-2">
                            <Label>ICD-10 Diagnosis Codes</Label>
                            <div className="flex flex-wrap gap-1.5" data-testid="sb-icd-chips">
                                {icdCodes.map((c) => (
                                    <span key={c} className="inline-flex items-center gap-1 rounded-full bg-[#FFF4EC] border border-orange-200 px-2.5 py-1 text-xs font-medium text-[#C2410C]">
                                        {c} <button type="button" data-testid={`sb-icd-remove-${c}`} onClick={() => setIcdCodes(icdCodes.filter((x) => x !== c))}><X className="h-3 w-3" /></button>
                                    </span>
                                ))}
                            </div>
                            <div className="relative">
                                <Input data-testid="sb-icd-search" value={icdQ} onChange={(e) => setIcdQ(e.target.value)} placeholder="Search ICD-10 — O80, N92, PCOS..." />
                                {icdResults.length > 0 && (
                                    <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-44 overflow-y-auto" data-testid="sb-icd-results">
                                        {icdResults.filter((r) => !icdCodes.includes(`${r.code}`)).map((r) => (
                                            <button key={r.code} type="button" data-testid={`sb-icd-option-${r.code}`} onClick={() => { setIcdCodes([...icdCodes, `${r.code} ${r.label}`]); setIcdQ(""); setIcdResults([]); }} className="w-full text-left px-4 py-2 hover:bg-[#FFF4EC] border-b border-slate-100 last:border-0 text-sm">
                                                <b className="font-mono2 text-[#C2410C]">{r.code}</b> <span className="text-slate-600">{r.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="space-y-2" data-testid="sb-items">
                            <div className="flex items-center justify-between">
                                <Label>Line Items *</Label>
                                <Button type="button" size="sm" variant="outline" data-testid="sb-add-item-btn" onClick={() => setItems([...items, { ...EMPTY_ITEM }])} className="gap-1 text-xs"><Plus className="h-3.5 w-3.5" /> Add Item</Button>
                            </div>
                            {items.map((it, i) => (
                                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`sb-item-${i}`}>
                                    <Input data-testid={`sb-item-desc-${i}`} value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} placeholder="Consultation / procedure" className="col-span-5" />
                                    <Input data-testid={`sb-item-code-${i}`} value={it.code} onChange={(e) => setItem(i, "code", e.target.value)} placeholder="Code" className="col-span-2" />
                                    <Input data-testid={`sb-item-qty-${i}`} type="number" min="0.5" step="0.5" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} className="col-span-2" />
                                    <Input data-testid={`sb-item-rate-${i}`} type="number" min="0" value={it.rate} onChange={(e) => setItem(i, "rate", e.target.value)} placeholder="₹ rate" className="col-span-2" />
                                    <button type="button" data-testid={`sb-item-remove-${i}`} onClick={() => setItems(items.filter((_, idx) => idx !== i))} className="col-span-1 text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                                </div>
                            ))}
                            <p className="text-right text-sm font-heading font-bold text-slate-900" data-testid="sb-total">Total: ₹{total.toLocaleString("en-IN")}</p>
                        </div>

                        <div className="space-y-1.5"><Label>Notes</Label><Input data-testid="sb-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="TPA details, remarks..." /></div>
                        <Button data-testid="sb-submit-btn" type="submit" disabled={saving || !selected} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create Superbill</Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
